import {

  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useFeedbackModal } from '../../contexts/FeedbackModalContext';
import {
  SubmissionConfirmation,
  FeedbackFormFields,
  FeedbackDialogActions,
  useFeedbackSubmit,
  useFeedbackForm,
} from './feedbackModal';

export default function FeedbackModal() {
  const { isOpen, defaultType, closeFeedbackModal } = useFeedbackModal();

  const {
    submitting,
    errorMessage,
    setErrorMessage,
    submissionResult,
    copied,
    handleCopyReference,
    referenceText,
    userName,
    userEmail,
    userPhone,
    userRole,
    accountId,
    linkedAccountIds,
    metadata,
    envLabel,
    handleSubmit: submitReport,
    resetFormState,
  } = useFeedbackSubmit(isOpen);

  const {
    type,
    title,
    setTitle,
    description,
    setDescription,
    screenshotFile,
    screenshotPreview,
    fileInputRef,
    handleTypeChange,
    handleFileChange,
    handleRemoveScreenshot,
  } = useFeedbackForm({
    isOpen,
    defaultType,
    resetFormState,
    setErrorMessage,
  });

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    submitReport({
      type,
      title,
      description,
      screenshotFile,
    });
  };

  const handleClose = () => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    closeFeedbackModal();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={!submitting ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      aria-labelledby="feedback-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            p: { xs: 1, sm: 2 },
          },
        },
      }}
    >
      <DialogTitle
        id="feedback-dialog-title"
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={800} color="primary.main">
          {submissionResult ? 'Feedback Submitted' : 'Report an Issue / Suggest a Feature'}
        </Typography>
        <Tooltip title="Close dialog" describeChild>
          <span>
            <IconButton
              aria-label="Close dialog"
              onClick={handleClose}
              disabled={submitting}
              size="small"
            >
              <Close />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {submissionResult ? (
          <SubmissionConfirmation
            submissionResult={submissionResult}
            type={type}
            referenceText={referenceText}
            userEmail={userEmail}
            copied={copied}
            onCopyReference={handleCopyReference}
          />
        ) : (
          <FeedbackFormFields
            type={type}
            onTypeChange={handleTypeChange}
            title={title}
            onTitleChange={setTitle}
            description={description}
            onDescriptionChange={setDescription}
            errorMessage={errorMessage}
            onClearError={() => setErrorMessage('')}
            submitting={submitting}
            fileInputRef={fileInputRef}
            screenshotFile={screenshotFile}
            screenshotPreview={screenshotPreview}
            onFileChange={handleFileChange}
            onRemoveScreenshot={handleRemoveScreenshot}
            onSubmit={onSubmit}
            userName={userName}
            userEmail={userEmail}
            userPhone={userPhone}
            userRole={userRole}
            accountId={accountId}
            linkedAccountIds={linkedAccountIds}
            route={metadata.route}
            environment={envLabel}
          />
        )}
      </DialogContent>

      <FeedbackDialogActions
        submissionResult={submissionResult}
        onClose={handleClose}
        onSubmit={onSubmit}
        submitting={submitting}
        canSubmit={Boolean(title.trim() && description.trim())}
      />
    </Dialog>
  );
}
